import { useCallback, useEffect, useRef } from 'react';
import {
  useBackButtonState,
  useMenuBarState,
  useNavbarActions,
  useNavigationState,
  useSearchState,
} from 'src/pages/consumer/components/DishFirstHeader/store';
import { useFeatureFlag } from 'src/shared/design-system/FeatureFlag';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { defaultClientRect, NAV_HEIGHT_VALUE } from '../consts';
import { useWindowScroll } from './useWindowScroll';

export type UseShefMenuHeaderProps = {
  showMenuBar?: boolean;
  showFiltersInline?: boolean;
};

export const useShefMenuHeader = (props: UseShefMenuHeaderProps) => {
  const { showMenuBar } = props;
  const { backButton, filterBar, inlineFilterButtons, menuBar, navigation, promo, setIsScrollingUp } =
    useNavbarActions();
  const isBackButtonShowing = useBackButtonState();
  const isMenuBarShowing = useMenuBarState();
  const isNavigationShowing = useNavigationState();
  const isSearchShowing = useSearchState();
  const inlineFiltersRef = useRef<HTMLDivElement>(null);
  const menuBarRef = useRef<HTMLDivElement>(null);

  const { isSmallScreen } = useScreenContext();
  const isBulkDiscountEnabled = useFeatureFlag('bulkDiscountEnabled');
  const hideFilterBar = isSmallScreen && isBulkDiscountEnabled;

  const handleScroll = useCallback(
    (isWindowScrollingUp: boolean) => {
      const { top: inlineFiltersTop } = inlineFiltersRef.current?.getBoundingClientRect() ?? defaultClientRect;
      const { top: menuBarTop } = menuBarRef.current?.getBoundingClientRect() ?? defaultClientRect;
      setIsScrollingUp(isWindowScrollingUp);

      if (isSearchShowing) {
        return;
      }

      if (inlineFiltersTop != null && inlineFiltersTop < NAV_HEIGHT_VALUE && !hideFilterBar) {
        filterBar.show();
        inlineFilterButtons.show();
      } else {
        inlineFilterButtons.hide();
        filterBar.hide();
      }
      if (showMenuBar && menuBarTop != null && menuBarTop < NAV_HEIGHT_VALUE) {
        menuBar.show();
      } else if (showMenuBar || (!showMenuBar && isMenuBarShowing)) {
        menuBar.hide();
      }
    },
    [
      filterBar,
      hideFilterBar,
      inlineFilterButtons,
      isMenuBarShowing,
      isSearchShowing,
      menuBar,
      setIsScrollingUp,
      showMenuBar,
    ]
  );

  useWindowScroll(handleScroll);

  useEffect(() => {
    promo.show();

    if (!isNavigationShowing) {
      navigation.show();
    }
    if (!isBackButtonShowing) {
      backButton.show();
    }
    if (isMenuBarShowing && !showMenuBar) {
      menuBar.hide();
    }
  });

  return { inlineFiltersRef, menuBarRef };
};
